import React, {useEffect, useMemo, useState} from 'react';
import fileDownload from "js-file-download";
import {TabBar, TabPanel, Tooltip} from "@atttomyx/react-components";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Badge,
    Button,
    Card,
    CircularProgress,
    IconButton,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import {
    CheckBox as CompleteIcon,
    CheckBoxOutlineBlank as IncompleteIcon,
    Download as DownloadIcon,
    Edit as EditIcon,
    ExpandMore as AccordionIcon,
    MusicNote as ListenIcon,
    OpenInNew as GotoIcon,
    YouTube as WatchIcon,
} from "@mui/icons-material";
import {PlayMusicDialog, SubmissionDialog, YouTubeDialog} from "../dialogs";
import {documentService, homeworkService, magicService, progressService} from "../../services";
import {arrays, cloudinary, datetime, numbers, objects, sorting, strings} from "@atttomyx/shared-utils";
import {progressUtils, upsellUtils} from "../../utils";
import {MESSAGE_AI, PLACEHOLDER_LANDSCAPE, TEMPLATE_ID_BUSINESS_PLAN, TEMPLATE_ID_PITCH_DECK} from "../../constants";
import "./documentsCard.css";

const DocumentsCard = (props) => {
    const {
        snackbar, user, course, documentsById, videosById, upsellsById, questionnairesById,
        questionsByQuestionnaireId, submissionsByQuestionnaireId, filesByCourseId, progress,
        onSaveSubmission, onSaveFiles, onSaveProgress
    } = props;
    const [downloading, setDownloading] = useState(null);
    const [watching, setWatching] = useState(null);
    const [questionnaire, setQuestionnaire] = useState(null);
    const [showAudio, setShowAudio] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [tab, setTab] = useState(0);
    const [open, setOpen] = useState(false);

    const description = useMemo(() => {
        return arrays.splitOnNewline(course.description);
    }, [questionnaire]);

    const documents = useMemo(() => {
        const rank = user.settings.rank;
        const array = [];

        course.documentIds.forEach(documentId => {
            const document = documentsById[documentId];

            if (document && arrays.contains(document.ranks, rank)) {
                arrays.addTo(array, document);
            }
        });

        return array;
    }, [user, course, documentsById]);

    const videos = useMemo(() => {
        const rank = user.settings.rank;
        const array = [];

        course.videoIds.forEach(videoId => {
            const video = videosById[videoId];

            if (video && arrays.contains(video.ranks, rank)) {
                arrays.addTo(array, video);
            }
        });

        return array;
    }, [user, course, videosById]);

    const upsells = useMemo(() => {
        const array = [];

        course.upsellIds.forEach(upsellId => {
            const upsell = upsellsById[upsellId];

            if (upsell) {
                arrays.addTo(array, upsell);
            }
        });

        return array.sort(sorting.sortByOrder);
    }, [course, upsellsById]);

    const questionnaires = useMemo(() => {
        const array = [];

        course.questionnaireIds.forEach(questionnaireId => {
            const questionnaire = questionnairesById[questionnaireId];

            if (questionnaire) {
                arrays.addTo(array, questionnaire);
            }
        });

        return array.sort(sorting.sortByOrder);
    }, [course, questionnairesById]);

    const allSubmissions = useMemo(() => {
        const array = [];

        Object.values(submissionsByQuestionnaireId)
        .forEach(submission => {
            if (submission) {
                arrays.addTo(array, submission);
            }
        });

        return array;
    }, [submissionsByQuestionnaireId]);

    const submissions = useMemo(() => {
        const array = [];

        course.questionnaireIds.forEach(questionnaireId => {
            const submission = submissionsByQuestionnaireId[questionnaireId];

            if (submission) {
                arrays.addTo(array, submission);
            }
        });

        course.prerequisiteIds.forEach(prerequisiteId => {
            const submission = submissionsByQuestionnaireId[prerequisiteId];

            if (submission && !arrays.contains(array, submission)) {
                arrays.addTo(array, submission);
            }
        });

        return array;
    }, [course, submissionsByQuestionnaireId]);

    const files = useMemo(() => {
        return objects.defaultIfNullOrUndefined(filesByCourseId[course.id], []);
    }, [course, filesByCourseId]);

    const items = useMemo(() => {
        const array = [];

        arrays.addAll(array, documents);
        arrays.addAll(array, videos);

        return array.sort(sorting.sortByOrder);
    }, [documents, videos]);

    const questions = useMemo(() => {
        const array = [];

        if (questionnaire) {
            const questionsToUse = questionsByQuestionnaireId[questionnaire.id];

            if (questionsToUse) {
                arrays.addAll(array, questionsToUse);
            }
        }

        return array.sort(sorting.sortByOrder);
    }, [questionnaire, questionsByQuestionnaireId]);

    const contentComplete = useMemo(() => {
        let ret = !course.music || progressUtils.hasListenedTo(progress);

        if (ret) {
            items.forEach(item => {
                ret = ret && ((item.title && progressUtils.hasWatched(progress, item.id)
                    || (!item.title && progressUtils.hasDownloaded(progress, item.id))));
            });
        }

        return ret;
    }, [course, items, progress]);

    const homeworkComplete = useMemo(() => {
        let ret = questionnaires.length > 0;

        questionnaires.forEach(questionnaire => {
            ret = ret && submissionsByQuestionnaireId[questionnaire.id];
        });

        return ret;
    }, [questionnaires, submissionsByQuestionnaireId]);

    const submissionAt = useMemo(() => {
        let ret = null;

        questionnaires.forEach(questionnaire => {
            const submission = submissionsByQuestionnaireId[questionnaire.id];

            if (submission) {
                if (ret == null || submission.updated > ret) {
                    ret = submission.updated;
                }
            }
        });

        return ret;
    }, [questionnaires, submissionsByQuestionnaireId]);

    const qualified = useMemo(() => {
        const questionnaireIds = [];
        let ret = true;

        submissions.forEach(submission => {
            arrays.addTo(questionnaireIds, submission.questionnaireId);
        });

        course.prerequisiteIds.forEach(prerequisiteId => {
            if (ret && !arrays.contains(questionnaireIds, prerequisiteId)) {
                ret = false;
            }
        });

        return ret;
    }, [course, submissions]);

    const availableTabs = useMemo(() => {
        const available = [];

        if (course.music || course.documentIds.length > 0 || course.videoIds.length > 0) {
            arrays.addTo(available, "Content");
        }

        if (course.questionnaireIds.length > 0) {
            arrays.addTo(available, "Homework");
        }

        // it's always there because AI will create a Business Plan and Pitch Deck as soon as they submit any homework
        arrays.addTo(available, "Generated");

        if (course.upsellIds.length > 0) {
            arrays.addTo(available, "Upsells");
        }

        return available;
    }, [course]);

    const submittable = useMemo(() => {
        let ret = false;

        if (homeworkComplete) {
            const courseAt = progress ? progress.courseIds[course.id] : null;

            ret = courseAt == null || submissionAt > courseAt;
        }

        return ret;
    }, [progress, course, homeworkComplete, submissionAt]);

    useEffect(() => {
        if (!contentComplete || !homeworkComplete) {
            setOpen(true);
        }
    }, [contentComplete, homeworkComplete]);

    const download = (document) => {
        const success = (bytes) => {
            fileDownload(bytes, document.fileName);
            setDownloading(null);
            progressService.saveProgress(null, document.id, null, null, onSaveProgress, snackbar.setError);
        };

        const failure = (err) => {
            snackbar.setError(err);
            setDownloading(null);
        };

        setDownloading(document.id);
        documentService.downloadFile(document.id, success, failure);
    };

    const submitHomework = () => {
        const homework = {
            course: course,
            submissions: submissions,
        };

        const success = (result, message) => {
            onSaveFiles(result.documents);
            setSubmitting(false);
            setTab(2);

            snackbar.setSuccess(message ? message : "Generated " + strings.count("asset", result.documents.length));
            progressService.saveProgress(course.id, null, null, null, onSaveProgress, snackbar.setError);
        };

        const failure = (err) => {
            snackbar.setError(err);
            setSubmitting(false);
        };

        setSubmitting(true);
        homeworkService.submitHomework(homework, success, failure);
        magicService.createBusinessPlanAndPitchDeck(allSubmissions,
            (result) => success(result, "AI has updated your Business Plan and Pitch Deck"), failure);
    };

    return (
        <Badge
            color="error"
            variant="dot"
            invisible={contentComplete && homeworkComplete && !submittable}
        >
            <Card className="documents-card" raised={true}>
                <Accordion
                    expanded={open}
                    onChange={(event, expanded) => setOpen(expanded)}
                >
                    <AccordionSummary expandIcon={<AccordionIcon/>}>
                        <Typography variant="h5">
                            {course.name}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <img
                            className="course"
                            alt={course.name}
                            src={course.imageUrl ? cloudinary.trim(course.imageUrl) : PLACEHOLDER_LANDSCAPE}
                        />
                        {description.length > 0 ?
                            <div className="description">
                                {description.map(line => strings.isBlank(line) ? null :
                                    <Typography key={line} variant="caption" component="p">
                                        {line}
                                    </Typography>)}
                            </div> : null}
                        {course.gotoUrl ?
                            <Link
                                href={"https://g.page/r/Cf9NiDyvSWgDEAE/review?utm_source=gbp&utm_medium=reviews&utm_campaign=qr"}
                                color="primary"
                                target="upsell"
                                sx={{display: 'flex', alignItems: 'center', mt: 1}}
                            >
                                <GotoIcon sx={{mr: 1}}/>
                                {course.gotoLabel ?
                                    <Typography variant="caption">
                                        {course.gotoLabel}
                                    </Typography> : null}
                            </Link> : null}
                        {!qualified ?
                            <Typography variant="caption">
                                Please complete homework for access to additional content
                            </Typography> :
                            <>
                                <TabBar
                                    tabs={availableTabs}
                                    value={tab}
                                    onChange={setTab}
                                />
                                <TabPanel value={tab} index={0}>
                                    <Table size="small" cellPadding={0} cellSpacing={0} className="striped">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className="small"/>
                                                <TableCell>
                                                    Name
                                                </TableCell>
                                                <TableCell>
                                                    Size
                                                </TableCell>
                                                <TableCell>
                                                    Done
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {course.music ?
                                                <TableRow key="music">
                                                    <TableCell>
                                                        <IconButton color="primary" title="Watch"
                                                                    onClick={() => setShowAudio(true)}>
                                                            <ListenIcon/>
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell className="file">
                                                        Theme song
                                                    </TableCell>
                                                    <TableCell>
                                                        -
                                                    </TableCell>
                                                    <TableCell>
                                                        {progressUtils.hasListenedTo(progress) ?
                                                            <CompleteIcon/> :
                                                            <IncompleteIcon color="disabled"/>}
                                                    </TableCell>
                                                </TableRow> : null}
                                            {items.map(item => item.title ?
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        <IconButton color="primary" title="Watch"
                                                                    onClick={() => setWatching(item)}>
                                                            <WatchIcon/>
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell className="file">
                                                        {item.title}
                                                    </TableCell>
                                                    <TableCell>
                                                        -
                                                    </TableCell>
                                                    <TableCell>
                                                        {progressUtils.hasWatched(progress, item.id) ?
                                                            <CompleteIcon/> :
                                                            <IncompleteIcon color="disabled"/>}
                                                    </TableCell>
                                                </TableRow> :
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        {downloading === item.id ?
                                                            <CircularProgress size="24px"/> :
                                                            <IconButton color="primary" title="Download"
                                                                        onClick={() => download(item)}>
                                                                <DownloadIcon/>
                                                            </IconButton>}
                                                    </TableCell>
                                                    <TableCell className="file">
                                                        {item.fileName}
                                                    </TableCell>
                                                    <TableCell>
                                                        {numbers.abbreviateNumber(item.size)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {progressUtils.hasDownloaded(progress, item.id) ?
                                                            <CompleteIcon/> :
                                                            <IncompleteIcon color="disabled"/>}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TabPanel>
                                <TabPanel value={tab} index={1}>
                                    <Table size="small" cellPadding={0} cellSpacing={0} className="striped">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className="small"/>
                                                <TableCell>
                                                    Name
                                                </TableCell>
                                                <TableCell className="small">
                                                    Done
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {questionnaires.map(questionnaire =>
                                                <TableRow key={questionnaire.id}>
                                                    <TableCell>
                                                        <IconButton color="primary" title="Fill out"
                                                                    onClick={() => setQuestionnaire(questionnaire)}>
                                                            <EditIcon/>
                                                        </IconButton>
                                                    </TableCell>
                                                    <TableCell>
                                                        {questionnaire.title}
                                                    </TableCell>
                                                    <TableCell>
                                                        {submissionsByQuestionnaireId[questionnaire.id] ?
                                                            <CompleteIcon/> :
                                                            <IncompleteIcon color="disabled"/>}
                                                    </TableCell>
                                                </TableRow>)}
                                        </TableBody>
                                    </Table>
                                    {submitting ?
                                        <CircularProgress size="24px"/> :
                                        <Button
                                            color="primary"
                                            disabled={!submittable}
                                            onClick={submitHomework}
                                            className="submit"
                                        >
                                            Submit
                                        </Button>}
                                </TabPanel>
                                <TabPanel value={tab} index={2}>
                                    {!homeworkComplete || files.length < 1 ?
                                        <Typography variant="caption">
                                            Please submit homework to generate assets
                                        </Typography> :
                                        <Table size="small" cellPadding={0} cellSpacing={0} className="striped">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell className="small"/>
                                                    <TableCell>
                                                        Name
                                                    </TableCell>
                                                    <TableCell>
                                                        Size
                                                    </TableCell>
                                                    <TableCell>
                                                        Updated
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {files.map(file =>
                                                    <TableRow key={file.id}>
                                                        <TableCell>
                                                            {downloading === file.id ?
                                                                <CircularProgress size="24px"/> :
                                                                <IconButton color="primary" title="Download"
                                                                            onClick={() => download(file)}>
                                                                    <DownloadIcon/>
                                                                </IconButton>}
                                                        </TableCell>
                                                        <TableCell className="file">
                                                            {file.fileName}
                                                            {file.templateId === TEMPLATE_ID_BUSINESS_PLAN
                                                            || file.templateId === TEMPLATE_ID_PITCH_DECK ?
                                                                <span className="tooltip"><Tooltip
                                                                    message={MESSAGE_AI}/></span> : null}
                                                        </TableCell>
                                                        <TableCell>
                                                            {numbers.abbreviateNumber(file.size)}
                                                        </TableCell>
                                                        <TableCell className="timestamp">
                                                            {datetime.getPrettyDateAndTime(file.updated)}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    }
                                </TabPanel>
                                <TabPanel value={tab} index={3}>
                                    <Table size="small" cellPadding={0} cellSpacing={0} className="striped">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell className="small"/>
                                                <TableCell>
                                                    Name
                                                </TableCell>
                                                <TableCell>
                                                    Price
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {upsells.map(upsell =>
                                                <TableRow key={upsell.id}>
                                                    <TableCell>
                                                        {upsell.landingPageUrl ?
                                                            <Link href={upsell.landingPageUrl} color="primary" target="upsell">
                                                                <GotoIcon/>
                                                            </Link> : <GotoIcon color="disabled"/>}
                                                    </TableCell>
                                                    <TableCell className="file">
                                                        {upsell.name}
                                                        {upsell.description ?
                                                            <span className="tooltip"><Tooltip message={upsell.description}/></span> : null}
                                                    </TableCell>
                                                    <TableCell className="timestamp">
                                                        {upsellUtils.formatPrice(upsell.price)}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TabPanel>
                            </>}
                    </AccordionDetails>
                </Accordion>
                {showAudio ?
                    <PlayMusicDialog
                        onCancel={() => setShowAudio(false)}
                        onListened={() => progressService.saveProgress(null, null, null, true, onSaveProgress,
                            snackbar.setError)}
                    /> : null}
                {watching ?
                    <YouTubeDialog
                        title={watching.title}
                        videoId={watching.youTubeId}
                        onClose={() => setWatching(null)}
                        onWatched={() => progressService.saveProgress(null, null, watching.id, null, onSaveProgress,
                            snackbar.setError)}
                    /> : null}
                {questionnaire ?
                    <SubmissionDialog
                        snackbar={snackbar}
                        theme={user.settings.theme}
                        questionnaire={questionnaire}
                        questions={questions}
                        submission={submissionsByQuestionnaireId[questionnaire.id]}
                        onCancel={() => setQuestionnaire(null)}
                        onSave={(saved) => {
                            setQuestionnaire(null);
                            onSaveSubmission(saved);
                        }}
                    /> : null}
            </Card>
        </Badge>
    )
}

export default DocumentsCard;
